import React, { ReactElement, useState } from 'react';
import classNames from 'classnames';
import colors from 'constants/colors';
import { ModalComponent } from 'components/Modal';
import Icon, { IconNames } from 'components/Icon';
import QuantityInput from 'components/QuantityInput';
import { formatNumberAsCurrency, limitChars, parseCurrency } from 'helpers/utils';
import { ITicket } from 'store/pages/types';

interface DragNDropProps<T> {
  rows: T[];
  setRows: any;
  labels: (string | ReactElement)[];
  deleteRow: (ticketID: string) => void;
  deleteText: string;
  isPageDuplicated: boolean;
  priceHasError?: (row: T) => boolean;
}

export const DragNDrop = <T extends ITicket>({
  rows,
  setRows,
  labels,
  deleteRow,
  deleteText,
  isPageDuplicated,
  priceHasError,
}: DragNDropProps<T>) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [captionText, setCaptionText] = useState('');
  const [rowSelected, setRowSelected] = useState<T>();
  const tableWithQuantity = rows.length > 0 && Object.keys(rows[0] ?? {}).includes('quantity');

  const handleDragStart = (id: string) => {
    setDraggingId(id);
  };

  const handleDragOver = (event: React.DragEvent, id: string) => {
    event.preventDefault();

    if (draggingId === id) return;

    const draggedIndex = rows.findIndex(row => row.id === draggingId);
    const targetIndex = rows.findIndex(row => row.id === id);
    const updatedRows = [...rows];
    const [draggedRow] = updatedRows.splice(draggedIndex, 1);
    updatedRows.splice(targetIndex, 0, draggedRow);

    setRows(updatedRows);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };

  const handleInputChange = (id: string, field: keyof T, value: string | number) => {
    setRows(rows.map(row => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const showCaptionPopup = (row: T) => {
    setRowSelected(row);
    setCaptionText(row?.description || '');
    setIsOpen(true);
  };

  const closePopup = () => setIsOpen(false);

  const saveCaption = () => {
    handleInputChange(rowSelected?.id || '0', 'description', captionText);
    setCaptionText('');
    closePopup();
  };

  return (
    <>
      <ModalComponent isOpen={isOpen} closeModal={closePopup}>
        <span className="oath-eyebrow inkBlue">Add Ticket Caption</span>
        <span className="oath-body inkBlue">40 characters recommended.</span>
        <span className="oath-body inkBlue">100 characters max.</span>
        <input
          type="text"
          value={captionText}
          className="input-popup form-control"
          placeholder="Optional caption goes here"
          onChange={e => setCaptionText(e.target.value)}
        />
        <button type="button" className="btn-inkBlue btn btn-secondary" onClick={saveCaption}>
          Save caption
        </button>
        <button type="button" className="oath-bodysmall link-text inkBlue" onClick={closePopup}>
          Cancel
        </button>
      </ModalComponent>
      <table className="draggable-table">
        <thead>
          <tr>
            {labels.map((label, index) => (
              <th key={index} className={classNames({ 'quantity-th': !tableWithQuantity })}>
                <label>{label}</label>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={row.id}
              draggable
              onDragStart={() => handleDragStart(row.id)}
              onDragOver={event => handleDragOver(event, row.id)}
              onDragEnd={handleDragEnd}
              className={classNames('row-draggable', draggingId === row.id ? 'dragging' : '')}
            >
              <td>
                <input
                  type="text"
                  value={row.name}
                  placeholder="Enter ticket name"
                  className="ticket-input form-control"
                  onChange={e => handleInputChange(row.id, 'name', e.target.value)}
                />
              </td>
              <td
                className={
                  priceHasError ? (priceHasError(row) ? 'has-danger' : 'has-success') : undefined
                }
              >
                <input
                  type="text"
                  value={formatNumberAsCurrency(row.price)}
                  min={0}
                  className="ticket-input small form-control"
                  onFocus={e => e.target.select()}
                  onChange={e => handleInputChange(row.id, 'price', parseCurrency(e.target.value))}
                  onBlur={e => handleInputChange(row.id, 'price', parseCurrency(e.target.value))}
                />
              </td>
              {tableWithQuantity && (
                <td className={row.quantity > 0 ? 'has-success' : 'has-danger'}>
                  <QuantityInput
                    value={
                      row.id?.length > 2 && !isPageDuplicated
                        ? `${row.quantity} / ${row.quantity_remaining}`
                        : row.quantity
                    }
                    onChange={newQuantity => handleInputChange(row.id, 'quantity', newQuantity)}
                    min={0}
                    max={999}
                    disabled={row.id?.length > 2 && !isPageDuplicated}
                  />
                </td>
              )}
              <td>
                <div className="row align-center justify-between">
                  <span>{limitChars(row.description, 40)}</span>
                  <div className="text-right">
                    <span
                      className="oath-bodysmall link-text gray"
                      onClick={() => showCaptionPopup(row)}
                    >
                      {row.description ? 'Edit Caption' : 'Add Caption'}
                    </span>
                  </div>
                </div>
              </td>
              <td className="icon-td">
                <Icon name={IconNames.Menu} color={colors.gray} size={16} />
              </td>
              <td>
                <span
                  key={row.id}
                  className={classNames('oath-bodysmall link-text gray', {
                    disabled: rows.length === 1,
                  })}
                  onClick={() => rows.length > 1 && deleteRow(row.id)}
                >
                  {deleteText}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};
